import React, { useState } from 'react'
import Card from 'react-bootstrap/Card'
import { Stack, Tab, Tabs } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import { getDate } from '../services/TimeService'
import { formatCurrency } from '../services/currency'
import Pagination from './Pagination'
import {InvoiceData, TabKeys} from '../types'
import useTheme from '../hooks/useTheme'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import {
	setCurrentInvoice,
	setCurrentTab,
	setIncomingCurrentPage,
	setPendingCurrentPage,
	setProcessedCurrentPage,
} from '../store/invoicesSlice'
import {
	useGetIncomingInvoicesQuery,
	useGetPendingInvoicesQuery,
	useGetProcessedInvoicesQuery,
	useDeleteInvoiceMutation,
} from '../store/api'
import useHasPermission from '../hooks/useHasPermission'
import DeleteInvoiceModal from './DeleteInvoiceModal'
import { ReactComponent as DeleteIcon } from '../assets/icons/trash3-fill.svg'

const InvoiceListsBlock: React.FC = () => {
	const theme = useTheme()
	const dispatch = useAppDispatch()
	const hasInvoicesIndexPermission = useHasPermission('invoices.index')
	const hasInvoicesDeletePermission = useHasPermission('invoices.delete')

	const [deleteInvoiceId, setDeleteInvoiceId] = useState<number | null>(null)
	const [deleteInvoiceNumber, setDeleteInvoiceNumber] = useState<string | null>(null)

	const {
		currentInvoice,
		currentTab,
		incomingCurrentPage,
		pendingCurrentPage,
		processedCurrentPage,
	} = useAppSelector((state) => state.invoices)

	const {
		data: dataIncomingInvoices,
		isLoading: isLoadingIncomingInvoices,
	} = useGetIncomingInvoicesQuery(incomingCurrentPage)
	const {
		data: dataPendingInvoices,
		isLoading: isLoadingPendingInvoices,
	} = useGetPendingInvoicesQuery(pendingCurrentPage)
	const {
		data: dataProcessedInvoices,
		isLoading: isLoadingProcessedInvoices,
	} = useGetProcessedInvoicesQuery(processedCurrentPage)

	const [deleteInvoice, { isLoading: isLoadingDeleteInvoice }] = useDeleteInvoiceMutation()

	const handleSetCurrentInvoice = (invoice: InvoiceData) => {
		dispatch(setCurrentInvoice(invoice))
	}

	const handleDeleteClick = (e: React.MouseEvent, invoice: InvoiceData) => {
		e.stopPropagation()
		setDeleteInvoiceId(invoice.id)
		setDeleteInvoiceNumber(invoice.number || `#${invoice.id}`)
	}

	const handleDeleteConfirm = async () => {
		if (deleteInvoiceId) {
			try {
				await deleteInvoice(deleteInvoiceId).unwrap()
				if (currentInvoice?.id === deleteInvoiceId) {
					dispatch(setCurrentInvoice(null))
				}
				setDeleteInvoiceId(null)
				setDeleteInvoiceNumber(null)
			} catch (error) {
				console.error(error)
			}
		}
	}

	const handleDeleteClose = () => {
		setDeleteInvoiceId(null)
		setDeleteInvoiceNumber(null)
	}

	const handleSetCurrentTab = (tab: TabKeys) => {
		dispatch(setCurrentTab(tab))
	}

	const handleSetIncomingCurrentPage = (page: number) => {
		dispatch(setIncomingCurrentPage(page))
	}

	const handleSetPendingCurrentPage = (page: number) => {
		dispatch(setPendingCurrentPage(page))
	}

	const handleSetProcessedCurrentPage = (page: number) => {
		dispatch(setProcessedCurrentPage(page))
	}

	const invoiceBtns = (data: InvoiceData[] | undefined, currentInvoice:  InvoiceData | null) =>data?.map((invoice) => {
		return(
			<div
				key={invoice.id}
				className="position-relative mt-2 mb-2"
			>
				<Button
					onClick={() => handleSetCurrentInvoice(invoice)}
					active={currentInvoice?.id === invoice.id}
					variant={theme}
					className="w-100 d-flex flex-column align-items-start border-0"
					style={{ paddingRight: hasInvoicesDeletePermission ? '45px' : undefined }}
				>
					<h6>
						Заявка {invoice.number} - {invoice.buyer}
					</h6>
					<span className="text-start">
						<span className="opacity-75">
							{invoice.date_of_sale ? getDate(invoice.date_of_sale) : ''}
						</span>
						<br/>
						<span>
							{invoice.total_amount && invoice.currency
								? formatCurrency(invoice.total_amount, invoice.currency)
								: ''}
						</span>
					</span>
				</Button>
				{hasInvoicesDeletePermission && (
					<Button
						variant="link"
						size="sm"
						className="position-absolute top-0 end-0 m-1 d-flex align-items-center justify-content-center"
						style={{ 
							width: '32px', 
							height: '32px',
							padding: 0,
							border: 'none',
							background: 'transparent'
						}}
						onClick={(e) => handleDeleteClick(e, invoice)}
					>
						<DeleteIcon style={{ width: '16px', height: '16px', color: '#dc3545' }} />
					</Button>
				)}
			</div>
		)
	})


	const isLoading =
		isLoadingIncomingInvoices ||
		isLoadingPendingInvoices ||
		isLoadingProcessedInvoices

	if (!hasInvoicesIndexPermission) {
		return null
	}

	return (
		<Card
			className={`h-100 ${isLoading ? 'opacity-50' : ''}`}
		>
			<Card.Body>
				<Card.Title>Перелік заявок</Card.Title>
				<Tabs
					activeKey={currentTab}
					onSelect={(k) => handleSetCurrentTab(k as TabKeys)}
					id="uncontrolled-tab-example"
					className="mb-3"
				>
					<Tab eventKey={TabKeys.incoming} title="Нові заявки">
						<div>
							<Stack>
								{invoiceBtns(dataIncomingInvoices?.data, currentInvoice)}
								{dataIncomingInvoices?.data.length === 0 && (
									<p className="text-center mt-4">Нові заявки відсутні</p>
								)}
							</Stack>
							{dataIncomingInvoices && (
								<Pagination
									data={dataIncomingInvoices}
									setPage={handleSetIncomingCurrentPage}
								/>
							)}
						</div>
					</Tab>
					<Tab eventKey={TabKeys.pending} title="В обробці">
						<div>
							<Stack>
								{invoiceBtns(dataPendingInvoices?.data, currentInvoice)}
								{dataPendingInvoices?.data.length === 0 && (
									<p className="text-center mt-4">
										Заявки в обробці відсутні
									</p>
								)}
							</Stack>
							{dataPendingInvoices && (
								<Pagination
									data={dataPendingInvoices}
									setPage={handleSetPendingCurrentPage}
								/>
							)}
						</div>
					</Tab>
					<Tab eventKey={TabKeys.processed} title="Збережені">
						<div>
							<Stack>
								{invoiceBtns(dataProcessedInvoices?.data, currentInvoice)}
								{dataProcessedInvoices?.data.length === 0 && (
									<p className="text-center mt-4">
										Сбережені заявки відсутні
									</p>
								)}
							</Stack>
							{dataProcessedInvoices && (
								<Pagination
									data={dataProcessedInvoices}
									setPage={handleSetProcessedCurrentPage}
								/>
							)}
						</div>
					</Tab>
				</Tabs>
			</Card.Body>
			<DeleteInvoiceModal
				invoiceNumber={deleteInvoiceNumber}
				onClose={handleDeleteClose}
				isLoading={isLoadingDeleteInvoice}
				handleSubmit={handleDeleteConfirm}
			/>
		</Card>
	)
}

export default InvoiceListsBlock

